import { createSelector } from "@reduxjs/toolkit"
import { isUnderSiege } from "src/utils/siege"
import { redactForViewer } from "src/utils/fog"
import { effectiveForPlayer } from "src/utils/pendingOrders"

import { discountedOrderCost, researchOutput, researchPointMultiplier, researchPrice } from "src/utils/gameModifiers"
// Import the leaf selector modules, not the slice barrels. The barrels construct slices and pull
// in thunks -> apiClient -> the whole app graph, which makes this module impossible to unit test.
import { selectUserId } from "src/store/user/selectors"
import { selectGameConfig } from "src/store/common/selectors"
import constants from "src/constants"

export const selectGameSlice = (state) => state.game

/* The game every panel reads — and, for a live spectator "watching as" a seat (owner, 2026-09-29:
   "should see what exactly the player sees"), that seat's fog laid over it. The server sends a
   spectator the whole board; `utils/fog` is the same mirror of `games/visibility` the replay runs,
   so choosing a seat shows its fog, its discovered cells, its view of every march and its own
   events, and "Spectator" (`watchingThroughFog` false) is the board untouched. Applied here, once,
   rather than in the view, because the map, the roster, the modals and the digest all read the
   game from the store and a second, unfogged copy anywhere would be a leak on screen. The raw
   payload stays in the slice, so the socket keeps merging into the real thing. */
/* The live game — what the socket last sent, whatever is on screen. Read it for what must not be
   fooled by a past board: the round clock (`RoundHandler`, `TikTak`), the socket thunk's
   before/after comparisons, the digest and the round flash. */
export const selectLiveGame = createSelector([selectGameSlice], (gameSlice) => gameSlice.game)

/* Looking back at a round of a live game (requirement 35, H1–H8): `{tick, game, loading}` or null. */
export const selectHistory = (state) => state.game?.history ?? null
export const selectRoundTrail = (state) => state.game?.roundTrail ?? null
export const selectRoundMotion = (state) => state.game?.roundMotion ?? null

export const selectIsLookingBack = (state) => state.game?.history != null

/* While looking back (H2), the past board laid over the live game: that round's tick and metadata
   — its marches at their origins, its cells, its events, fogged by the server as it was then —
   under the live game's seats, host and status, which are live state (a ready dot, a seat that
   fell since). A round whose board has aged out, or is still loading, keeps the live board. */
export const selectRawGame = createSelector(
  [selectLiveGame, selectHistory, (state) => state.game?.motions],
  (game, history, motions) => {
    if (!game) return game
    if (history?.game) {
      return {
        ...game,
        tick: history.game.tick,
        tickTime: history.game.tickTime ?? game.tickTime,
        metadata: history.game.metadata,
      }
    }
    // The live board with the log that produced it (`requirements/36` P3), kept since it arrived:
    // the broadcasts after an action carry none.
    const motion = motions?.[String(game.id)]?.[game.tick]
    if (!game.metadata || game.metadata.motion || !motion) return game
    return { ...game, metadata: { ...game.metadata, motion } }
  }
)

export const selectWatchingThroughFog = (state) => Boolean(state.game.watchingThroughFog)

export const selectGame = createSelector(
  [
    selectRawGame,
    selectWatchingThroughFog,
    // A past round of the live game is a played round, fogged as one (`redactForViewer`'s `past`).
    (state) => Boolean(state.game?.history?.game),
    (state) => state.game.spectatingAs ?? null,
    (state) => state.common?.gameConfig,
    // Read softly: a store with no user slice (a bare component test, a signed-out page) has no
    // player whose holds to take off.
    (state) => state.user?.id,
  ],
  (game, throughFog, past, seat, gameConfig, userId) => {
    if (!game?.metadata) return game
    if (throughFog && seat != null) {
      const metadata = redactForViewer({ ...game.metadata, tick: game.tick }, seat, gameConfig?.rules_config, { past })
      // Exactly what that player sees: their holds come off too (the orders are the team's, so
      // the redaction has kept them).
      return effectiveForPlayer({ game: { ...game, metadata }, gameConfig, userId: seat })
    }
    // The player's own numbers as they may spend them (owner, 2026-10-02): what a level or a
    // colony ordered this round holds is off the bank and the workers (`effectiveForPlayer`).
    return seat == null ? effectiveForPlayer({ game, gameConfig, userId }) : game
  }
)

/* Every live game this account is in — the home page's list, and where the route guard finds
   the game its URL names. */
export const selectGames = createSelector([selectGameSlice], (gameSlice) => gameSlice.games ?? [])

/* Whose board is being drawn.

   For every ordinary game this is the logged-in player and nothing below changes. A spectator who
   has picked a seat, and a replay being read as one of the players, set `spectatingAs` — and then
   "my cities", "my food", "the events addressed to me" answer for *that* seat, which is what makes
   a replay watchable as a game rather than as a map of coloured squares (owner's ask, 2026-09-04).

   The distinction this file has to keep is display against permission. Everything here is display:
   what to draw, and for whom. Whether an order may be *sent* is the logged-in player's business and
   stays on `selectUserId` — the Game view keeps both, and a borrowed seat never grows a hand. */
export const selectSpectatingAs = createSelector([selectGameSlice], (gameSlice) => gameSlice.spectatingAs ?? null)

export const selectViewerId = createSelector(
  [selectSpectatingAs, selectUserId],
  (spectatingAs, userId) => spectatingAs ?? userId
)

/* Looking, not playing. True exactly when a seat has been borrowed — a replay, or a spectator who
   has picked somebody to watch through — and it is what the panels ask before drawing a hand.

   It is deliberately *not* the same question as `isSpectator`. A spectator with no seat picked has
   nothing to act on and nothing to show; a replay viewer has a whole board to inspect and must not
   be offered a single button that could reach a socket. Every panel below the map (city, building,
   production) shows the same information either way and hides its actions on this. */
/* ...and while a player looks back at a past round of a live game (H4): the board is the past, so
   every panel that hides its orders on this hides them there too. */
export const selectIsReviewing = createSelector(
  [selectSpectatingAs, selectIsLookingBack],
  (spectatingAs, lookingBack) => spectatingAs != null || lookingBack
)

export const selectReactions = (state) => state.game.reactions

export const selectChatMessages = createSelector([selectGameSlice], (gameSlice) => gameSlice.chatMessages)

export const selectMetadataCells = createSelector([selectGame], (game) => game?.metadata?.cells)

export const selectPlayerMetadataCells = createSelector([selectViewerId, selectMetadataCells], (userId, cells) => {
  const playerCells = {}
  // Fog of war blanks metadata_by_user on cells the player cannot see into, so both the map and
  // each entry have to be treated as possibly absent.
  Object.entries(cells || {}).forEach(([coordinates, cellData]) => {
    if (cellData?.metadata_by_user?.[userId]) {
      playerCells[coordinates] = cellData.metadata_by_user[userId]
    }
  })
  return playerCells
})

export const selectPlayer = createSelector([selectGame, (_, userId) => userId], (game, userId) =>
  game?.players.find((player) => player.user.id === userId)
)

export const selectCityMetadataByUser = createSelector(
  [selectMetadataCells, (_, coordinates) => coordinates],
  (cells, coordinates) => cells?.[`${coordinates.col}-${coordinates.row}`] || null
)

export const selectPlayerTotalUnits = createSelector([selectGame, selectViewerId], (game, userId) => {
  // units_per_user is recomputed per recipient under fog of war, so an enemy key is simply absent.
  return Object.entries(game?.metadata?.units_per_user?.[String(userId)] || {}).map(([unitType, number]) => ({
    type: unitType,
    number,
  }))
})

export const selectGamePlayers = createSelector([selectGame], (game) => game?.players || [])

export const selectCurrentPlayer = createSelector([selectGame, selectViewerId], (game, userId) =>
  game?.players.find((player) => player.user.id === userId)
)

// The main production building differs per team: humans queue on the command center, zombies on
// the hatchery. Everything below keys off this.
export const selectMainBuildingCode = createSelector([selectCurrentPlayer], (player) =>
  player?.team === "human" ? constants.buildings.human.COMMAND_CENTER : constants.buildings.zombie.HATCHERY
)

// The units this player's race may build. The mapping is authoritative on the backend and reaches
// us through GET /game-config/ — never duplicate it in constants.js, it drifts.
export const selectValidUnitsForPlayer = createSelector(
  [selectGameConfig, selectCurrentPlayer],
  (gameConfig, player) => {
    if (!gameConfig || !player) return []
    // gameConfig is stored verbatim from GET /game-config/, so keys stay snake_case here.
    return gameConfig.valid_units_per_race?.[player.race] || []
  }
)

const selectCityMetadataForCurrentPlayer = createSelector(
  [selectMetadataCells, selectViewerId, (_, coordinates) => coordinates],
  (cells, userId, coordinates) => cells?.[`${coordinates.col}-${coordinates.row}`]?.metadata_by_user?.[userId] || null
)

// Pre-fills the production panel: re-opening the modal must show what is already queued, not zeroes.
export const selectCityProductionQueue = createSelector(
  [selectCityMetadataForCurrentPlayer, selectMainBuildingCode],
  (cityMetadata, mainBuildingCode) => {
    const queue = cityMetadata?.buildings?.[mainBuildingCode]?.queue || {}
    return Object.fromEntries(Object.entries(queue).map(([unitType, data]) => [unitType, data.number]))
  }
)

export const selectCityMainBuildingLevel = createSelector(
  [selectCityMetadataForCurrentPlayer, selectMainBuildingCode],
  (cityMetadata, mainBuildingCode) => cityMetadata?.buildings?.[mainBuildingCode]?.level || 0
)

// Humans pay for units in workers held on that specific cell; zombies pay from the player-level
// meat pool. Two different currencies — requirement 02 is explicit that they must not be conflated.
export const selectCityWorkers = createSelector(
  [selectCityMetadataForCurrentPlayer],
  (cityMetadata) => cityMetadata?.units?.[constants.units.human.WORKER]?.number || 0
)

// The player's own units on one cell, shaped for UnitSelector's `available` badge.
export const selectCityUnitsForPlayer = createSelector([selectCityMetadataForCurrentPlayer], (cityMetadata) =>
  Object.entries(cityMetadata?.units || {}).map(([type, data]) => ({ type, available: data.number }))
)

export const selectPlayerFood = createSelector([selectGame, selectViewerId], (game, userId) =>
  Number(game?.metadata?.users?.[String(userId)]?.food ?? 0)
)

export const selectIsCurrentPlayerReady = createSelector([selectCurrentPlayer], (player) => !!player?.ready)

// Research points: a third resource that accrues every tick and, until now, appeared nowhere.
export const selectResearchPoints = createSelector([selectGame, selectViewerId], (game, userId) =>
  Number(game?.metadata?.users?.[String(userId)]?.research_points ?? 0)
)

// The research building a player's team produces points from, and the best one they have.
//
// It starts at level 0, so research points never accrue until someone upgrades it — two players in
// game 9 sat at zero for twelve ticks without a hint that the whole technology tree was switched
// off. Level 0 across every city is the state worth saying out loud. Requirement 18d.
export const selectResearchBuildingCode = createSelector([selectCurrentPlayer], (player) =>
  player?.team === "human" ? constants.buildings.human.ACADEMY : constants.buildings.zombie.EVOLUTION_CHAMBER
)

export const selectBestResearchBuildingLevel = createSelector(
  [selectMetadataCells, selectViewerId, selectResearchBuildingCode],
  (cells, userId, buildingCode) =>
    Object.values(cells || {}).reduce((best, cell) => {
      const level = cell?.metadata_by_user?.[userId]?.buildings?.[buildingCode]?.level || 0
      return Math.max(best, level)
    }, 0)
)

export const selectResearchLevels = createSelector(
  [selectGame, selectViewerId],
  (game, userId) => game?.metadata?.users?.[String(userId)]?.researches || {}
)

// The catalogue is authoritative on the backend and reaches us through GET /game-config/ as
// `researches: {human: [...], zombie: [...]}` (S17, 2026-09-05). It used to be read off
// `research_max_level`, which since the ceilings came off names only the two keys. `maxLevel` is
// null for an endless line; `cost` is the doubling every line follows (`researchPrice`).
export const selectResearchCatalogue = createSelector(
  [selectGameConfig, selectCurrentPlayer, selectResearchLevels],
  (gameConfig, player, levels) => {
    if (!gameConfig || !player) return []
    const rules = gameConfig.rules_config || {}
    const maxLevels = gameConfig.research_max_level || {}
    return (gameConfig.researches?.[player.team] || []).map((name) => {
      const key = `${player.team}:${name}`
      const level = levels[name] || 0
      const maxLevel = maxLevels[key] ?? null
      return {
        name,
        level,
        maxLevel,
        cost: researchPrice({ rules, level, research: name }),
        maxed: maxLevel != null && level >= maxLevel,
      }
    })
  }
)

// What happened in the round just processed. The server scopes these per recipient, so anything
// present here is something this player is entitled to know.
/* Raw metadata events → what the report renders. Pure and exported: the live panel reads it via
   the selector below, and the round-history view runs it over past snapshots from /steps/. */
export const displayRoundEvents = (events, userId, viewerTeam = null, players = []) =>
  (events || []).map((event) => {
      const seatOf = (id) => players.find((seat) => Number(seat?.user?.id) === Number(id)) || null
      const eliminatedSeat = event.type === "player_eliminated" ? seatOf(event.detail?.user_id) : null
      const sightedSeat = event.type === "enemy_sighted" ? seatOf(event.detail?.user_id) : null
      // A wave's newcomer (SV11): the event names the bot by id, and everything a player reads
      // about it — the name, the class, how hard it plays — is on the seat the server has already
      // put in `players`. The bots keep arriving mid-game, so that row appears with the event.
      const appearedSeat = event.type === "enemy_appeared" ? seatOf(event.detail?.user_id) : null
      const [column, row] = event.coordinates
      const losses = event.detail?.losses || {}
      const lossesByUnit = event.detail?.losses_by_unit || {}
      const mergeByUnit = (mine) => {
        const merged = {}
        for (const [id, byUnit] of Object.entries(lossesByUnit)) {
          if ((id === String(userId)) !== mine) continue
          for (const [unit, dead] of Object.entries(byUnit || {})) {
            if (Number(dead) > 0) merged[unit] = (merged[unit] || 0) + Number(dead)
          }
        }
        return merged
      }
      // Whom the event is *about* (owner's R23): the fighters of a battle, the loser of a cell,
      // the starving garrison's owner, the march that was held. Read off the detail where it
      // names anyone, else off the audience — the report labels a teammate's events with their
      // name and filters "mine" from "the team's" by this.
      const detail = event.detail || {}
      const subjectIds = [
        ...new Set(
          [
            ...Object.keys(detail.losses || {}),
            ...Object.keys(detail.losses_by_unit || {}),
            detail.from_user,
            detail.to_user,
            detail.by_user,
            detail.user_id,
            ...(detail.victims || []),
            typeof detail.movement_id === "string" ? detail.movement_id.split("-")[0] : null,
          ]
            .filter((id) => id != null && id !== "")
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id))
        ),
      ]
      if (subjectIds.length === 0) subjectIds.push(...(event.visible_to || []).map(Number))
      return {
        type: event.type,
        column,
        row,
        subjectIds,
        concernsMe: subjectIds.includes(Number(userId)),
        /* The side the event is about, when every subject is on one — off the seats, not the
           viewer. A spectator reads every seat's events, and a starving horde drew the corn of
           whichever human was being watched (owner, 2026-09-29): a glyph that names a side's
           food has to be the side that went hungry. */
        subjectTeam: (() => {
          const teams = new Set(subjectIds.map((id) => seatOf(id)?.team).filter(Boolean))
          return teams.size === 1 ? [...teams][0] : null
        })(),
        eliminatedUserId: event.type === "player_eliminated" ? Number(detail.user_id) : null,
        eliminatedName: eliminatedSeat?.user?.username ?? null,
        eliminatedIsMe: event.type === "player_eliminated" && Number(detail.user_id) === Number(userId),
        eliminatedIsAlly:
          event.type === "player_eliminated" &&
          Number(detail.user_id) !== Number(userId) &&
          eliminatedSeat?.team != null &&
          eliminatedSeat.team === viewerTeam,
        // Split "us" from "them" here rather than in the component, which should not have to know
        // that losses are keyed by user id as strings.
        ownLosses: Number(losses[String(userId)] ?? 0),
        enemyLosses: Object.entries(losses)
          .filter(([id]) => id !== String(userId))
          .reduce((total, [, dead]) => total + Number(dead), 0),
        // What the blast (or battle) actually hit, unit by unit — the wording layer turns this
        // into "3 Zombies, 1 Cursed" after the totals.
        ownLossesByUnit: mergeByUnit(true),
        enemyLossesByUnit: mergeByUnit(false),
        wallAbsorbed: Boolean(event.detail?.wall_absorbed),
        // A missile that landed on a square with nobody on it (owner, 2026-10-02: a strike lands a
        // round after it is fired, and the stack it was aimed at may not have stayed to meet it).
        hitNobody: event.type === "missile_strike" && Object.keys(losses).length === 0,
        // A shot held back because the square became our own side's before it landed: nothing
        // hit, nothing lost, said so rather than vanishing.
        spared: Boolean(event.detail?.spared),
        // upgrade_cancelled / founding_cancelled (owner, 2026-10-02): an order of last round that
        // did not land — which building, and why ("lost", "unaffordable", "taken", "left",
        // "contested"). The server sends the code; the wording layer says it.
        cancelledBuilding: event.detail?.building ?? null,
        cancelReason: event.detail?.reason ?? null,
        // tunnel_cancelled (owner, 2026-10-05): which way the dropped order went, "load" or "unload".
        tunnelDirection: event.detail?.direction ?? null,
        // battle / battle_ended: the meat the round's (or the whole battle's) kills paid each
        // zombie player (owner's U5, 2026-09-07) — reported by the server as it was credited,
        // never re-derived here from the dead.
        meatWonByUser: event.detail?.meat_won || {},
        ownMeatWon: Number(event.detail?.meat_won?.[String(userId)] ?? 0),
        // battle: which round of the engagement this event narrates, so a report line can open
        // the battle view pinned to the very round it describes.
        battleRound: Number(event.detail?.round ?? 0),
        capturedByUs: event.detail?.to_user === userId,
        lostByUs: event.detail?.from_user === userId,
        // cell_lost: an enemy city this player walked into empty (owner, 2026-10-04: *"when I
        // steal a city that had no troops, there is no notification"*). The server names who stood
        // there when the deed dissolved; a capture moves no deed since Q16, so this is the only
        // word the taker gets.
        takenByUs:
          event.detail?.reason === "conquered" && (event.detail?.by_users || []).map(Number).includes(Number(userId)),
        starved: Number(event.detail?.starved ?? 0),
        // march_halted: `late` means the stack reached a battle that broke out earlier in this
        // same round, so it joins the fight next round (a battle carried over from an earlier
        // round is fought after the movement loop, with everybody — owner, 2026-10-03). A march
        // stopped by a fight it could not finish carries no flag — two reasons to stand still,
        // and the player is told which.
        haltedLate: Boolean(event.detail?.late),
        firedByUs: event.detail?.by_user === userId,
        // battle_ended: the whole engagement in one event — see BattleUnitsSummary's report shape.
        winnerTeam: event.detail?.winner_team ?? null,
        wonByUs: event.detail?.winner_team != null && event.detail?.winner_team === viewerTeam,
        rounds: Number(event.detail?.rounds ?? 0),
        lossesByUnitByUser: event.detail?.losses_by_unit || {},
        lastRound: event.detail?.last_round || null,
        // enemy_sighted (owner, 2026-09-07): an enemy stack that moved this round now stands in
        // our vision — how many, whose, and the cell it set out from.
        sightedUnits: Number(event.detail?.units ?? 0),
        sightedFrom: Array.isArray(event.detail?.came_from)
          ? { column: Number(event.detail.came_from[0]), row: Number(event.detail.came_from[1]) }
          : null,
        sightedFighting: Boolean(event.detail?.fighting),
        sightedName: sightedSeat?.user?.username ?? null,
        /* enemy_appeared (SV11, 2026-09-16): a survival wave landing. The coordinates are the
           bot's first crown — which is where the line's door pings and where the tour flies —
           and the rest of its cells ride in the detail so the map can ring them all for the
           round. `wave` is 0 for the board's opening, which is worded as "at the start" rather
           than as a wave nobody lived through. */
        appearedName: appearedSeat?.user?.username ?? null,
        // Who the wave sent it for (29/F4, owner 2026-09-20). The event carries the id and the
        // seat carries the name, the same split every other id on this payload keeps.
        appearedTarget:
          (event.type === "enemy_appeared"
            ? seatOf(event.detail?.target_user_id)?.user?.username
            : null) ?? null,
        appearedRace: appearedSeat?.race ?? null,
        appearedDifficulty: appearedSeat?.botDifficulty ?? null,
        appearedWave: Number(event.detail?.wave ?? 0),
        appearedCities:
          (event.detail?.crowns?.length ?? 0) + (event.detail?.colonies?.length ?? 0),
      }
    })

export const selectRoundEvents = createSelector([selectGame, selectViewerId], (game, userId) =>
  displayRoundEvents(
    game?.metadata?.events,
    userId,
    game?.metadata?.users?.[String(userId)]?.team ?? null,
    game?.players || []
  )
)

export const selectIsGameFinished = createSelector([selectGame], (game) => game?.status === "finished")

export const selectWinnerTeam = createSelector([selectGame], (game) => game?.winnerTeam || null)

/* Who won it, by name (SV14). Only a survival game has one — there the whole side wins and one
   person outlasts the rest — and it is null on a draw and in every classic game, where the side
   is the answer. `{id, username}` or null. */
export const selectWinnerUser = createSelector([selectGame], (game) => game?.winnerUser || null)

/* The verdict on the seat being watched: "win" | "loss" | "draw", or null — a game still running,
   or somebody with no seat at all, which is what a spectator is. Read off the row rather than
   derived from the winning side, because in survival the whole side is the winner's. */
export const selectViewerOutcome = createSelector(
  [selectGame, selectViewerId],
  (game, userId) => game?.players?.find((player) => player.user.id === userId)?.outcome ?? null
)

export const selectActionsUsed = createSelector([selectGame, selectViewerId], (game, userId) =>
  Number(game?.metadata?.users?.[String(userId)]?.actions_used ?? 0)
)

// In-flight troop movements. `path` is a list of [column, row] pairs with the origin at index 0,
// and `current_index` is the stack's position on it right now.
export const selectMovements = createSelector([selectGame], (game) => game?.metadata?.movements || [])

/* Every player's game metadata, keyed by user id as a string — what tells a screen which side a
   cell's occupier is on. `cellActions` reads the same object straight off the game in `Game.js`;
   this is it as a selector, for the screens that are not handed the game itself. One frozen empty
   object stands in when there is no game, so a consumer memoising on it is not re-run every render
   by a fresh `{}`. */
const NO_USERS = Object.freeze({})
export const selectMetadataUsers = createSelector([selectGame], (game) => game?.metadata?.users || NO_USERS)

/* What the empire earns in points a round, which is what `apply_research` actually pays: it walks
   EVERY cell the player holds, adds `research_output(level)` for each research building on it
   (`RESEARCH_PER_LEVEL × RESEARCH_OUTPUT_GROWTH ** level`, doubling per level since 2026-09-29 —
   it read `RESEARCH_PER_LEVEL * level` before, which was already wrong once the building ladder
   went to `scale`), and multiplies the total by the owner's race bonus.

   The research modal was quoting `RESEARCH_PER_LEVEL * <the best single building's level>` and
   naming that building beside it (owner, 2026-09-10: "the investigation button should only show
   the total points, based on all bases"). With two chambers at level 1 the pool grew by 50 a round
   and the header said 25, and the name made a per-player pool read as one building's output.

   Two things it has to keep, both from the server: **a besieged city produces nothing**, points
   included (owner, 2026-09-08 — the same `is_under_siege` skip the three `collect_*` steps use),
   and the race multiplier, which is the Technologist's and the Intelligent's "+25%". */
export const selectResearchPerRound = createSelector(
  [
    selectGameConfig,
    selectMetadataCells,
    selectMetadataUsers,
    selectMovements,
    selectViewerId,
    selectResearchBuildingCode,
    selectCurrentPlayer,
  ],
  (gameConfig, cells, users, movements, userId, buildingCode, player) => {
    const rules = gameConfig?.rules_config
    const earned = Object.entries(cells || {}).reduce((total, [coords, cell]) => {
      if (String(cell?.owner) !== String(userId)) return total
      const level = cell?.metadata_by_user?.[userId]?.buildings?.[buildingCode]?.level || 0
      if (!level) return total
      if (isUnderSiege({ coords, cells, users, movements })) return total
      return total + researchOutput({ rules, level })
    }, 0)
    return Math.trunc(earned * researchPointMultiplier({ gameConfig, race: player?.race }))
  }
)

export const selectMovementsForDisplay = createSelector(
  [selectMovements, selectViewerId, selectGame],
  (movements, userId, game) =>
  movements.map((movement) => {
    const [originColumn, originRow] = movement.path[0]
    const [destColumn, destRow] = movement.path[movement.path.length - 1]
    // Three relations, not two (2026-08-21): "not mine" used to mean "enemy", so a teammate's
    // march arrived red, with its composition hidden, and tripped the under-attack alarm. The map
    // has always drawn self/ally/enemy properly (utils/maps.js); this is the same rule.
    const users = game?.metadata?.users || {}
    const myTeam = users[String(userId)]?.team
    const owner = users[String(movement.user_id)]?.team
    const relation =
      movement.user_id === userId ? "self" : owner != null && owner === myTeam ? "ally" : "enemy"
    const isEnemy = relation === "enemy"

    // The server publishes this, recomputed every tick by the same loop that advances the stack.
    // It used to be derived here as ceil(cellsLeft / speed), which is optimistic over water —
    // water costs double, and only the server knows where the water is.
    const roundsLeft = Math.max(1, movement.ticks_remaining ?? 0)

    // `inPlace` was the arrived *infiltration*: a stack that had reached its target and stayed a
    // movement, with no ETA to report because it was already there. Infiltration was removed on
    // 2026-09-10 and nothing else is in that state — a march that has arrived and is still a
    // movement is one fighting at the gates (2026-08-27), which is not "in position".

    return {
      id: movement.id,
      isEnemy,
      relation,
      halted: movement.halted,
      inPlace: false,
      type: "Movement",
      roundsLeft,
      origin: { column: originColumn, row: originRow },
      destination: { column: destColumn, row: destRow },
      // Where it still has to walk, current position included (Q15): a city on an enemy march's
      // *route* is as much under attack as the one at its end — the march stops and fights the
      // moment it steps onto a garrisoned cell.
      remainingPath: movement.path
        .slice(movement.current_index)
        .map(([column, row]) => ({ column, row })),
      // Composition as the server sent it — every march the viewer sees, an enemy's included
      // (36/V1, 2026-10-26). Nothing is re-hidden here.
      units: Object.entries(movement.units || {})
        .map(([type, data]) => ({ type, number: data?.number ?? 0 }))
        .filter((unit) => unit.number > 0),
      totalUnits: Object.values(movement.units || {}).reduce((total, data) => total + (data?.number ?? 0), 0),
    }
  })
)

// Teammates other than yourself. Empty in a 1v1, which is what gates trading: the backend rejects
// giving to yourself or across teams, so an empty list means the action has no legal target.
export const selectAllies = createSelector([selectGamePlayers, selectCurrentPlayer], (players, player) => {
  if (!player) return []
  return players.filter((other) => other.team === player.team && other.user.id !== player.user.id)
})

export const selectCurrentTeam = createSelector([selectCurrentPlayer], (player) => player?.team || null)

// What the player still has on order, across all their cities.
//
// `enqueue_troops` sets a finite order (owner's change, 2026-08-27): what is built comes off the
// queue, and an emptied queue stops producing. Playing a game to tick 24 showed what that costs when it is invisible — one queue of 100
// soldiers kept spending 100 workers a tick, upkeep passed income, and the city ended with 145
// soldiers and no population left. The queue was on the wire the whole time; it just lived inside a
// modal you had to open the right city to see. Requirement 18d.
export const selectStandingProduction = createSelector(
  [
    selectMetadataCells,
    selectViewerId,
    selectMainBuildingCode,
    selectGameConfig,
    selectCurrentPlayer,
    selectResearchLevels,
  ],
  (cells, userId, mainBuildingCode, gameConfig, player, researchLevels) => {
    const empty = { units: 0, cost: 0, byUnit: {}, cities: [] }
    if (!player) return empty

    // Humans pay in workers held on the producing cell, zombies from the player-level meat pool —
    // the same split ProductionPanel makes. Never add the two together.
    const costKey = player.team === "human" ? "COST_IN_WORKERS" : "COST"
    const configKey = player.team === "human" ? "human_config" : "zombie_config"
    const troopConfig = gameConfig?.[configKey]?.TROOPS || {}

    return Object.entries(cells || {}).reduce((standing, [coordinates, cell]) => {
      const queue = cell?.metadata_by_user?.[userId]?.buildings?.[mainBuildingCode]?.queue
      const entries = Object.entries(queue || {}).filter(([, data]) => (data?.number || 0) > 0)
      if (entries.length === 0) return standing

      const units = entries.reduce((total, [, data]) => total + data.number, 0)
      // Discounted, like the production panel: Military technology is what the server charges by,
      // and quoting list price here overstates what the standing order costs every round — the one
      // number this panel exists to make visible.
      const listPrice = entries.reduce(
        (total, [unitType, data]) => total + (troopConfig[unitType]?.[costKey] || 0) * data.number,
        0
      )
      const cost = discountedOrderCost({ listPrice, gameConfig, team: player.team, researchLevels })
      // What is on order, not only how much (owner, 2026-09-07): the chip has to be able to say
      // "50 soldiers in Yopal", and a bare total can say neither half of that.
      const byUnit = Object.fromEntries(entries.map(([unitType, data]) => [unitType, data.number]))
      return {
        units: standing.units + units,
        cost: standing.cost + cost,
        byUnit: entries.reduce(
          (totals, [unitType, data]) => ({ ...totals, [unitType]: (totals[unitType] || 0) + data.number }),
          standing.byUnit
        ),
        cities: [...standing.cities, { coordinates, units, cost, byUnit }],
      }
    }, empty)
  }
)

// The battles visible right now: cells where both teams have troops standing — the same rule the
// minimap burns by, so under fog this only names fights the player can actually see. The round
// comes from the server's battle clock when it has one (a fresh contact has no entry yet).
/* Where a fight is burning right now. **The one place that decides that**, and everything that
   draws a fire reads it: the roster row, the minimap, the radar chip.

   Two sources, and the second is why this had to become a selector rather than a presence check.
   Two teams standing on one cell is a battle. But since 2026-08-27 a besieging attacker waits at
   the gates as a *movement*, so one side of that fight lives outside `metadata_by_user` entirely
   and the presence check misses it — `metadata.battles` is the engine's own clock and is
   authoritative while a fight burns. The big map already knew that and read both; the minimap read
   only the first and so quietly stopped drawing sieges (owner, 2026-09-04). */
export const selectActiveBattles = createSelector([selectGame], (game) => {
  const users = game?.metadata?.users || {}
  const teamOf = (id) => users[String(id)]?.team
  const rounds = game?.metadata?.battles || {}
  const keys = new Set(Object.keys(rounds))
  for (const [key, cell] of Object.entries(game?.metadata?.cells || {})) {
    const teams = new Set()
    for (const [userId, metadata] of Object.entries(cell.metadata_by_user || {})) {
      if (Object.values(metadata?.units || {}).some((unit) => (unit?.number ?? 0) > 0)) {
        const team = teamOf(userId)
        if (team) teams.add(team)
      }
    }
    if (teams.size > 1) keys.add(key)
  }
  return [...keys]
    .map((key) => {
      const [col, row] = key.split("-").map(Number)
      return { col, row, round: rounds[key] ?? 0 }
    })
    .sort((a, b) => b.round - a.round)
})

